/**
 * The single, shared profile-edit form (D1) — avatar + name + bio, persisted via
 * `PATCH /users/:id`.
 *
 * This is the ONE source of truth for editing the signed-in user's profile. Both
 * the Synthase settings panel ({@link SettingsPanel} → its Profile section) and
 * the reusable {@link ProfileModal} render it, so there is never a second,
 * divergent avatar/bio editor. It is self-contained: it seeds its drafts from the
 * signed-in user (`useAuth`), saves through the user resource, and reflects the
 * change locally (`setUser`) so the chat avatar / profile view update immediately
 * without a refetch.
 *
 * @module
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import type { UserProfile } from '@molecule/app-auth'
import { MAX_AVATAR_SRC_LENGTH, resolveUserAvatar } from '@molecule/app-ide-react'
import { useAuth, useHttpClient, useTranslation } from '@molecule/app-react'
import { getClassMap } from '@molecule/app-ui'
import { Alert, Avatar, Button, Flex, Icon, Input, Textarea } from '@molecule/app-ui-react'

import { useIsMobile } from '../hooks/useMediaQuery.js'
import { serverErrorMessage } from '../lib/server-error.js'

/**
 * The signed-in user as this form edits it. `UserProfile` has `avatar`/`name` but
 * no `bio`; the user resource (`PATCH /users/:id`) accepts `name`, `avatar`, and
 * `bio`, so we widen it locally with the nullable `bio` field the API persists.
 */
export type ProfileUser = UserProfile & { bio?: string | null }

/**
 * Maximum bio length, mirroring the user resource's server-side cap
 * (`@molecule/api-resource-user` `MAX_BIO_LENGTH`). The API is the real enforcer;
 * this is a client-side UX guard (the cross-stack rule forbids importing the api
 * package). The avatar cap is imported from `@molecule/app-ide-react`
 * (`MAX_AVATAR_SRC_LENGTH`), the package that owns the chat-avatar render.
 */
const MAX_BIO_LENGTH = 1000

/** Maximum name length — a UX guard matching the project-title field. */
const MAX_NAME_LENGTH = 120

/**
 * Target for the auto-downscale: encode a little under the hard cap so the
 * base64 data-URI (which the API length-checks) always clears
 * `MAX_AVATAR_SRC_LENGTH` with headroom.
 */
const AVATAR_FIT_TARGET = Math.floor(MAX_AVATAR_SRC_LENGTH * 0.92)

/** Longest-side pixel dimensions tried, largest first, when shrinking to fit. */
const AVATAR_MAX_SIDES = [512, 384, 256, 192, 128]

/** Encode qualities tried per dimension, best first. */
const AVATAR_QUALITIES = [0.85, 0.7, 0.55, 0.4]

/** Does this browser actually encode WEBP via `canvas.toDataURL`? */
const canEncodeWebp = (() => {
  try {
    const c = document.createElement('canvas')
    c.width = c.height = 1
    return c.toDataURL('image/webp').startsWith('data:image/webp')
  } catch (_error) {
    // Canvas/encoding unavailable (e.g. SSR/JSDOM) — treat WEBP as unsupported.
    return false
  }
})()

/**
 * Loads any image `data:` URI into an `HTMLImageElement`. Rejects if the source
 * cannot be decoded as an image.
 */
const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('decode-failed'))
    img.src = src
  })

/**
 * Ensures an avatar `data:` URI fits under {@link MAX_AVATAR_SRC_LENGTH} by
 * downscaling + re-encoding on a canvas, rather than rejecting oversized images.
 *
 * Images already under the cap are returned untouched (preserves PNG
 * transparency, SVG, animated GIF). Oversized raster images are drawn onto a
 * canvas at successively smaller dimensions and qualities until the encoded
 * data-URI clears the target. WEBP is used when the browser encodes it (keeps
 * alpha); otherwise JPEG on a white background.
 *
 * @returns the fitted `data:` URI, or `null` if even the smallest/lowest-quality
 *   encode cannot get under the cap (in practice unreachable for real photos).
 */
const fitAvatarToLimit = async (dataUri: string): Promise<string | null> => {
  if (dataUri.length <= MAX_AVATAR_SRC_LENGTH) return dataUri

  const img = await loadImage(dataUri)
  const srcW = img.naturalWidth || img.width
  const srcH = img.naturalHeight || img.height
  if (!srcW || !srcH) return null

  const mimeType = canEncodeWebp ? 'image/webp' : 'image/jpeg'
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  for (const maxSide of AVATAR_MAX_SIDES) {
    const scale = Math.min(1, maxSide / Math.max(srcW, srcH))
    canvas.width = Math.max(1, Math.round(srcW * scale))
    canvas.height = Math.max(1, Math.round(srcH * scale))
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    if (mimeType === 'image/jpeg') {
      // JPEG has no alpha — flatten transparency onto white.
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    }
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    for (const quality of AVATAR_QUALITIES) {
      const out = canvas.toDataURL(mimeType, quality)
      if (out.length <= AVATAR_FIT_TARGET) return out
    }
  }
  return null
}

/** Props for {@link ProfileEditor}. */
export interface ProfileEditorProps {
  /** Fired after the profile is successfully saved. */
  onSaved?: () => void
  /**
   * Optional action rendered at the RIGHT of the bottom row (e.g. the profile
   * modal's Log out button).
   */
  secondaryAction?: React.ReactNode
  /**
   * When provided, the host renders the "Saved" confirmation itself (e.g. next
   * to its section heading) from this signal, and the editor renders none.
   */
  onSavedChange?: (saved: boolean) => void
}

/**
 * Renders the shared avatar + name + bio edit form for the signed-in user.
 *
 * @param props - {@link ProfileEditorProps}.
 * @returns The profile-edit form.
 */
/** The quiet inline save confirmation (green check + "Saved"). */
export function SavedIndicator() {
  const cm = getClassMap()
  const { t } = useTranslation()
  return (
    <span
      data-mol-id="profile-saved"
      className={cm.cn(cm.textSize('sm'), cm.textSuccess)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <Icon name="check" size={16} aria-hidden="true" />
      {t('settings.profile.saved', undefined, { defaultValue: 'Saved' })}
    </span>
  )
}

export function ProfileEditor({ onSaved, secondaryAction, onSavedChange }: ProfileEditorProps) {
  const cm = getClassMap()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const { user, setUser } = useAuth<ProfileUser>()
  const http = useHttpClient()

  // Drafts seed from the signed-in user; saving PATCHes the real user resource so
  // the avatar shows in the Synthase chat (Workspace threads `user.avatar` into
  // <ChatPanel userAvatar=…>) and the name/bio persist. `''` clears a field.
  const [avatar, setAvatar] = useState<string>(user?.avatar || '')
  const [name, setName] = useState<string>(user?.name || '')
  const [bio, setBio] = useState<string>(user?.bio || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const avatarFileInputRef = useRef<HTMLInputElement>(null)
  const avatarPreview = resolveUserAvatar(avatar) ?? undefined

  // Auto-clear the "Saved" confirmation after a beat, and mirror it to the
  // host (the settings panel shows it beside the section heading).
  useEffect(() => {
    onSavedChange?.(saved)
    if (!saved) return
    const timer = setTimeout(() => setSaved(false), 2500)
    return () => clearTimeout(timer)
  }, [saved, onSavedChange])

  // AUTO-SAVE: there is no Save button — edits persist after a short idle
  // debounce. Drafts only re-seed on mount, so the setUser() inside
  // handleSave can't clobber in-progress typing; once the save lands, the
  // user object matches the drafts and `dirty` goes false.
  const dirty =
    !!user &&
    (name !== (user.name || '') || avatar !== (user.avatar || '') || bio !== (user.bio || ''))
  useEffect(() => {
    if (!dirty || saving) return
    const timer = setTimeout(() => void handleSaveRef.current(), 800)
    return () => clearTimeout(timer)
  }, [name, avatar, bio, dirty, saving])

  /**
   * Reads a chosen image file as an inline `data:` URI and stages it as the
   * avatar draft, rejecting anything over the renderable/persistable cap. No
   * external upload bond is required for an MVP — small inline avatars round-trip
   * through the same `data:image/*` path the chat render already accepts.
   */
  const handleAvatarFile = useCallback(
    (file: File) => {
      setSaved(false)
      const reader = new FileReader()
      reader.onload = () => {
        const result = typeof reader.result === 'string' ? reader.result : ''
        // Scheme-only gate on the RAW input. We must NOT run resolveUserAvatar()
        // here: it also rejects anything over MAX_AVATAR_SRC_LENGTH, which is
        // exactly the oversized image we are about to downscale — gating on it
        // first made the resize below dead code. The size cap is enforced on the
        // FITTED result instead (resolveUserAvatar(fitted), below).
        if (!/^data:image\//i.test(result)) {
          setError(
            t('settings.profile.avatarInvalid', undefined, {
              defaultValue: 'That image could not be read as an avatar. Try a PNG, JPG, or WEBP.',
            }),
          )
          return
        }
        // Oversized images are auto-downscaled to fit the inline data-URI cap
        // rather than rejected. Small images pass through untouched.
        void fitAvatarToLimit(result)
          .then((fitted) => {
            // Final safety gate: scheme + size, on the fitted result.
            if (!fitted || !resolveUserAvatar(fitted)) {
              setError(
                t('settings.profile.avatarTooLarge', undefined, {
                  defaultValue: "That image couldn't be resized to fit. Try a different one.",
                }),
              )
              return
            }
            setError('')
            setAvatar(fitted)
          })
          .catch(() => {
            setError(
              t('settings.profile.avatarInvalid', undefined, {
                defaultValue: 'That image could not be read as an avatar. Try a PNG, JPG, or WEBP.',
              }),
            )
          })
      }
      reader.onerror = () => {
        setError(
          t('settings.profile.avatarReadFailed', undefined, {
            defaultValue: 'Failed to read the selected image.',
          }),
        )
      }
      reader.readAsDataURL(file)
    },
    [t],
  )

  const handleSave = useCallback(async () => {
    if (!user) return
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      // Body-direct (the user resource reads `req.body.name`/`avatar`/`bio`);
      // '' clears server-side.
      await http.patch(`/users/${user.id}`, { name, avatar, bio })
      // Reflect locally without a refetch so the chat avatar updates immediately.
      setUser({ ...user, name: name || undefined, avatar: avatar || undefined, bio: bio || null })
      setSaved(true)
      onSaved?.()
    } catch (err) {
      setError(
        serverErrorMessage(err) ??
          (err instanceof Error
            ? err.message
            : t('settings.profile.saveFailed', undefined, {
                defaultValue: 'Failed to save profile.',
              })),
      )
    } finally {
      setSaving(false)
    }
  }, [user, http, name, avatar, bio, setUser, onSaved, t])

  // Latest-save ref so the debounce effect never captures a stale closure.
  const handleSaveRef = useRef(handleSave)
  handleSaveRef.current = handleSave

  return (
    <div className={cm.stack(4)}>
      <Flex align="center" gap="md">
        <Avatar
          src={avatarPreview}
          name={name || user?.email}
          size={64}
          alt={t('settings.profile.avatarAlt', undefined, { defaultValue: 'Your avatar' })}
          testId="profile-avatar-preview"
        />
        <div className={cm.stack(2)}>
          <input
            ref={avatarFileInputRef}
            type="file"
            accept="image/*"
            data-mol-id="profile-avatar-file"
            className={cm.srOnly}
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) handleAvatarFile(file)
              // Reset so re-selecting the same file fires onChange again.
              e.target.value = ''
            }}
          />
          <Flex gap="sm">
            <Button
              variant="outline"
              size="sm"
              data-mol-id="profile-avatar-upload"
              onClick={() => avatarFileInputRef.current?.click()}
            >
              {t('settings.profile.uploadAvatar', undefined, { defaultValue: 'Upload' })}
            </Button>
            {avatar && (
              <Button
                variant="ghost"
                size="sm"
                data-mol-id="profile-avatar-remove"
                onClick={() => {
                  setAvatar('')
                  setSaved(false)
                  setError('')
                }}
              >
                {t('settings.profile.removeAvatar', undefined, { defaultValue: 'Remove' })}
              </Button>
            )}
          </Flex>
          <p className={cm.cn(cm.textSize('xs'), cm.textMuted)}>
            {t('settings.profile.avatarHint', undefined, {
              defaultValue: 'PNG, JPG, or WEBP — large images are resized automatically.',
            })}
          </p>
        </div>
      </Flex>

      <div>
        <label htmlFor="profile-name" className={cm.formLabelSmall}>
          {t('settings.profile.nameLabel', undefined, { defaultValue: 'Name' })}
        </label>
        <Input
          id="profile-name"
          data-mol-id="profile-name"
          value={name}
          onChange={(e) => {
            setName((e.target as HTMLInputElement).value)
            setSaved(false)
          }}
          maxLength={MAX_NAME_LENGTH}
          size="sm"
          // Left-aligned to match the Bio textarea below (the bond's input
          // default is the centered floating-label style).
          align="left"
          // ≥16px on phones so iOS Safari doesn't zoom the page on focus
          // (size="sm" resolves to 15px); desktop keeps the compact size.
          style={isMobile ? { fontSize: 16 } : undefined}
          placeholder={t('settings.profile.namePlaceholder', undefined, {
            defaultValue: 'Your display name',
          })}
        />
      </div>

      <div>
        <label htmlFor="profile-bio" className={cm.formLabelSmall}>
          {t('settings.profile.bioLabel', undefined, { defaultValue: 'Bio' })}
        </label>
        <Textarea
          id="profile-bio"
          data-mol-id="profile-bio"
          // Same size tier as the Name input above → identical font size.
          size="sm"
          // Grows with its content instead of scrolling inside a fixed box.
          autoResize
          value={bio}
          onChange={(e) => {
            setBio((e.target as HTMLTextAreaElement).value)
            setSaved(false)
          }}
          rows={3}
          maxLength={MAX_BIO_LENGTH}
          placeholder={t('settings.profile.bioPlaceholder', undefined, {
            defaultValue: 'A short bio about you.',
          })}
        />
      </div>

      {error && (
        <Alert status="error" data-mol-id="profile-error">
          {error}
        </Alert>
      )}

      {/* No Save button: edits auto-save (debounced). The "Saved" confirmation
          renders where the host chose — via onSavedChange (the settings panel
          puts it beside the section heading), else inline here beside the
          optional secondaryAction (the profile modal's Log out). */}
      {(secondaryAction || !onSavedChange) && (
        <Flex justify={secondaryAction ? 'between' : 'end'} align="center">
          <span>{!onSavedChange && saved && <SavedIndicator />}</span>
          {secondaryAction}
        </Flex>
      )}
    </div>
  )
}

ProfileEditor.displayName = 'ProfileEditor'
